import React, { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";

/**
 * Sección de vídeo del proyecto (YouTube embed con autoplay-mute on-scroll).
 *
 * 👉 Para cambiar el vídeo en el futuro: solo edita YOUTUBE_ID con el ID nuevo.
 *    (de https://youtu.be/XXXXXXXXXXX, copia los caracteres después de "/")
 */
const YOUTUBE_ID = "7J8pUA6_y9w";

export default function Video() {
  const containerRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // Cargar el iframe cuando la sección entre en el viewport (autoplay-mute)
  useEffect(() => {
    if (!containerRef.current || loaded) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
          setLoaded(true);
          observer.disconnect();
        }
      },
      { threshold: [0, 0.4, 1] }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [loaded]);

  const thumbnail = `https://i.ytimg.com/vi/${YOUTUBE_ID}/maxresdefault.jpg`;
  const embedUrl =
    `https://www.youtube.com/embed/${YOUTUBE_ID}` +
    `?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1` +
    `&loop=1&playlist=${YOUTUBE_ID}`;

  const handleManualPlay = () => setLoaded(true);

  return (
    <section
      id="video"
      data-testid="video-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-xs uppercase tracking-[0.2em] font-medium text-cyan-400">
            En primera persona
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-4 leading-[1.05]">
            Te cuento <span className="text-gradient">mi proyecto</span>.
          </h2>
        </div>

        <div
          ref={containerRef}
          data-testid="video-container"
          className="relative aspect-video rounded-[2rem] overflow-hidden bg-slate-900 border border-white/10 shadow-2xl group"
        >
          {/* Glow accent */}
          <div className="absolute -inset-px rounded-[2rem] bg-gradient-to-br from-cyan-400/20 via-transparent to-rose-500/20 -z-10 blur-2xl" />

          {loaded ? (
            <iframe
              src={embedUrl}
              title="Te cuento mi proyecto – Sin Aletas No Hay Paraíso"
              loading="lazy"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              data-testid="project-video-iframe"
            />
          ) : (
            <button
              type="button"
              onClick={handleManualPlay}
              data-testid="video-play-button"
              aria-label="Reproducir vídeo"
              className="absolute inset-0 w-full h-full cursor-pointer"
            >
              <img
                src={thumbnail}
                alt="Vista previa del vídeo del proyecto"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = `https://i.ytimg.com/vi/${YOUTUBE_ID}/hqdefault.jpg`;
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-slate-950/20" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-cyan-500/90 backdrop-blur-sm flex items-center justify-center shadow-[0_0_40px_rgba(34,211,238,0.4)] transition-transform group-hover:scale-110">
                  <Play className="w-9 h-9 text-slate-950 ml-1" fill="currentColor" />
                </div>
              </div>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
